import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Buildings, ArrowLeft, Lock, Check, Warning } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.put(`${API_URL}/api/auth/verify-reset-token`, { token });
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4">
        <div className="w-full max-w-md bg-white border border-zinc-200 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 mb-4">
            <Warning size={32} className="text-red-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-zinc-900 mb-2">
            Enlace Inválido
          </h1>
          <p className="text-zinc-600 mb-6">
            El enlace de recuperación es inválido o ha expirado. 
            Por favor, solicita uno nuevo.
          </p>
          <Link
            to="/forgot-password"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="request-new-link-btn"
          >
            Solicitar Nuevo Enlace
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4">
        <div className="w-full max-w-md bg-white border border-zinc-200 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 mb-4">
            <Check size={32} className="text-emerald-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-zinc-900 mb-2">
            Contraseña Actualizada
          </h1>
          <p className="text-zinc-600 mb-6">
            Tu contraseña ha sido actualizada correctamente. 
            Ya puedes iniciar sesión con tu nueva contraseña.
          </p>
          <Link
            to="/login"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="go-to-login-btn"
          >
            Ir al Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4">
      <div className="w-full max-w-md bg-white border border-zinc-200 p-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Buildings size={28} weight="bold" className="text-zinc-900" />
            <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">
              Nueva Contraseña
            </h1>
          </div>
          <p className="text-zinc-500 text-sm">
            Ingresa tu nueva contraseña para <strong>{email}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="alert-error text-sm" data-testid="reset-error">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="password" className="form-label">
              Nueva Contraseña
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input pl-10"
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="form-label">
              Confirmar Contraseña
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form-input pl-10"
                placeholder="Repite la contraseña"
                required
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="reset-submit-btn"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Actualizando...
              </>
            ) : (
              'Actualizar Contraseña'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"
          >
            <ArrowLeft size={16} />
            Volver al Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
